import { useMe, useUpdateMe, useUsers } from "@vidario/api-client";
import { formatDateDE, userInitials, type Role, type User } from "@vidario/domain";
import { Avatar, Button, Card, cn, Input, useToast } from "@vidario/ui";
import { Plus, Trash2 } from "lucide-react";
import { useState, type ReactElement } from "react";

import { useRole } from "../../guards/useRole";
import { AddUserDialog } from "./AddUserDialog";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { QuotaCell } from "./QuotaCell";

type Tab = "profile" | "users";

/** Einstellungen (MVP_requirements.md §3.2 / §4). Recruiter sees "Mein
 * Profil"; admin gets a second "Nutzerverwaltung" tab with the team
 * directory and an invite dialog that creates the user and sends the
 * setup-link mail via POST /users/invite/. */
export function SettingsRoute(): ReactElement {
  const role = useRole();
  const isAdmin = role === "admin";
  const { data: me } = useMe();
  const { data: users } = useUsers();
  const toast = useToast();
  const updateMe = useUpdateMe();

  const [tab, setTab] = useState<Tab>("profile");
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [companyDraft, setCompanyDraft] = useState<string | null>(null);

  const canDeleteUsers = me?.isSuperuser === true;

  // Initialise the company draft from the loaded `me` once.
  const initialCompany = me?.company ?? "";
  const company = companyDraft ?? initialCompany;
  const companyDirty = isAdmin && company !== initialCompany;

  const saveProfile = async (): Promise<void> => {
    if (!companyDirty) return;
    try {
      await updateMe.mutateAsync({ company: company.trim() || null });
      setCompanyDraft(null);
      toast.push({
        kind: "success",
        title: "Gespeichert",
        message: "Ihr Unternehmen wurde aktualisiert.",
      });
    } catch (err) {
      toast.push({
        kind: "error",
        title: "Speichern fehlgeschlagen",
        message: err instanceof Error ? err.message : "Unbekannter Fehler.",
      });
    }
  };

  return (
    <main className="mx-auto max-w-4xl px-4 py-10 sm:px-8 pb-20">
      <h1 className="m-0 mb-6 text-3xl font-bold -tracking-[0.5px]">Einstellungen</h1>

      {isAdmin && (
        <div className="mb-6 flex gap-1 border-b border-border-default">
          {(
            [
              { id: "profile" as const, label: "Mein Profil" },
              { id: "users" as const, label: "Nutzerverwaltung" },
            ] satisfies { id: Tab; label: string }[]
          ).map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "-mb-px border-b-2 px-4.5 py-3 text-sm transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                  active
                    ? "border-primary-500 font-semibold text-primary-700"
                    : "border-transparent font-medium text-neutral-500 hover:text-neutral-800",
                )}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {tab === "profile" && me && (
        <Card>
          <div className="mb-7 flex flex-wrap items-center gap-5 border-b border-border-default pb-5">
            <Avatar initials={userInitials(me.name)} size={64} />
            <div className="min-w-0 flex-1">
              <h3 className="m-0 text-lg font-semibold">{me.name}</h3>
              <div className="break-words text-[13px] text-text-tertiary">{me.email}</div>
            </div>
            <Button variant="secondary" size="sm" disabled title="Foto-Upload kommt später">
              Foto ändern
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Vorname"
              value={me.firstName}
              disabled
              helper="Wird vom Admin gesetzt"
              readOnly
            />
            <Input
              label="Nachname"
              value={me.lastName}
              disabled
              helper="Wird vom Admin gesetzt"
              readOnly
            />
            <div className="col-span-2">
              <Input
                label="E-Mail"
                value={me.email}
                disabled
                helper="Kann nicht geändert werden"
                readOnly
              />
            </div>
            <div className="col-span-2">
              <Input
                label="Unternehmen"
                value={company}
                onChange={(e) => setCompanyDraft(e.target.value)}
                disabled={!isAdmin}
                readOnly={!isAdmin}
                {...(!isAdmin && { helper: "Wird vom Admin gesetzt" })}
              />
            </div>
          </div>
          <div className="mt-6 flex items-center justify-between border-t border-border-default pt-5">
            <div>
              <div className="text-sm font-medium">Passwort</div>
              <div className="text-xs text-text-tertiary">
                Über „Passwort vergessen?“ auf dem Login-Screen einen Reset-Link anfordern.
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={() =>
                toast.push({
                  kind: "info",
                  title: "Passwort ändern",
                  message:
                    "Über „Passwort vergessen?“ auf dem Login-Screen einen Reset-Link anfordern.",
                })
              }
            >
              Passwort ändern
            </Button>
          </div>
          {isAdmin && (
            <div className="mt-5 flex justify-end">
              <Button
                onClick={() => void saveProfile()}
                disabled={!companyDirty}
                loading={updateMe.isPending}
              >
                Speichern
              </Button>
            </div>
          )}
        </Card>
      )}

      {tab === "users" && isAdmin && (
        <Card className="p-0" padded={false} data-testid="user-directory">
          <div className="flex items-center justify-between border-b border-border-default px-6 py-5">
            <div>
              <h3 className="m-0 text-base font-semibold">Nutzer</h3>
              <div className="mt-0.5 text-[13px] text-text-tertiary">
                {users?.length ?? 0} aktive Nutzer
              </div>
            </div>
            <Button leftIcon={<Plus size={16} />} onClick={() => setAddOpen(true)}>
              Nutzer einladen
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-neutral-50 text-left">
                  {(canDeleteUsers
                    ? ["Nutzer", "Rolle", "Suchen", "Unternehmen", "Angelegt", ""]
                    : ["Nutzer", "Rolle", "Suchen", "Unternehmen", "Angelegt"]
                  ).map((h, i) => (
                    <th
                      key={h || `col-${i}`}
                      className="border-b border-border-default px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users?.map((u) => {
                  const isSelf = me?.id === u.id;
                  return (
                    <tr
                      key={u.id}
                      className="border-b border-border-default last:border-b-0"
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <Avatar initials={userInitials(u.name)} size={32} />
                          <div>
                            <div className="font-medium">{u.name}</div>
                            <div className="text-xs text-text-tertiary">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <RoleBadge role={u.role} />
                      </td>
                      <td className="px-5 py-3.5">
                        <QuotaCell user={u} />
                      </td>
                      <td className="px-5 py-3.5 text-text-secondary">{u.company ?? "—"}</td>
                      <td className="tabular-nums px-5 py-3.5 text-text-tertiary">
                        {formatDateDE(u.createdAt)}
                      </td>
                      {canDeleteUsers && (
                        <td className="px-5 py-3.5 text-right">
                          <Button
                            variant="icon"
                            size="sm"
                            aria-label={`${u.name} löschen`}
                            title={
                              isSelf ? "Sie können sich nicht selbst löschen" : "Nutzer löschen"
                            }
                            disabled={isSelf}
                            onClick={() => setDeleteTarget(u)}
                            data-testid={`delete-user-${u.id}`}
                            // Button's "icon" variant only renders leftIcon/
                            // rightIcon — children are dropped. Pass the icon
                            // via leftIcon so it actually appears.
                            leftIcon={<Trash2 size={16} />}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <AddUserDialog open={addOpen} onOpenChange={setAddOpen} />
      <DeleteUserDialog
        user={deleteTarget}
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      />
    </main>
  );
}

function RoleBadge({ role }: { role: Role }): ReactElement {
  return role === "admin" ? (
    <span className="rounded-sm bg-[var(--admin-marker-bg)] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--admin-marker)]">
      Admin
    </span>
  ) : (
    <span className="rounded-sm bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-700">
      Recruiter
    </span>
  );
}
